import React from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { getPromoPricingActiveAsset } from '~/shared/utils/getPromoPricingActiveAsset';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useAsset } from '~/techstyle-shared/react-assets';
import {
  ProductPrice,
  useProductContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import featureNames from '../../constants/featureFlags';
import { promoPriceHiddenPromoTypes } from '../../constants/products';
import { getPromoPricingUIAssetContainerName } from '../../utils/promoPricingUI';
import useHasFeature from '../../utils/useHasFeature';

const DynamicFormattedMessage = FormattedMessage;

const Wrapper = styled.span``;

const PromoMessageWrapper = styled.span`
  ${({ $promoMessageStyle }) => $promoMessageStyle}
`;

const defaultGetPromoCodeMessageId = (promoCode) => {
  return `site_pdp_callout.price_label_${promoCode}`;
};

function PromoMessage({
  'data-autotag': dataAutotag,
  product: productFromProps,
  promoPriceStyle,
  promoMessageStyle,
  showPromoPrice: showPromoPriceFromProps = true,
  getPromoCodeMessageId = defaultGetPromoCodeMessageId,
  ...rest
}) {
  const productFromCtx = useProductContext();
  const { isVip } = useMembership();
  const product = productFromProps || productFromCtx;

  const promoCode = product?.promos?.promoCode;
  const promoTypeId = product?.promos?.promoTypeId;
  const promoLabel = product?.promos?.promoLabel || '';

  const showPromoPrice = showPromoPriceFromProps
    ? !promoPriceHiddenPromoTypes.has(promoTypeId)
    : undefined;

  const hasPromo = Boolean(promoCode) && typeof promoCode === 'string';

  const hasColon = promoLabel?.trim().endsWith(':');

  const promoPricingConfig = config.get('public.brand.promoPricingUI');
  const offerLabelContainerName = getPromoPricingUIAssetContainerName(
    promoPricingConfig,
    isVip
  );

  const assetContainerOfferLabel = useAsset(offerLabelContainerName);
  const isPrioritizedEnabled =
    useHasFeature(featureNames.PRIORITIZED_PROMOS) &&
    promoPricingConfig?.showPromoPricingOffer;

  const promoPricingActiveAsset = getPromoPricingActiveAsset({
    isPrioritizedEnabled,
    assetContainerOfferLabel,
    product,
  });

  const htmlText = promoPricingActiveAsset?.options?.htmlText?.trim();
  const gridLabel = promoPricingActiveAsset?.options?.customVars?.gridLabel;
  const offerLabel = `${htmlText}: ${gridLabel}`;

  const shouldShowOfferLabel =
    isPrioritizedEnabled && Boolean(htmlText || gridLabel);

  const shouldShowPromoLabel =
    (!shouldShowOfferLabel && promoCode && promoLabel) ||
    (!shouldShowOfferLabel && hasPromo);

  if (!hasPromo && !isPrioritizedEnabled) {
    return null;
  }

  return (
    <Wrapper tabIndex={0} {...rest}>
      <PromoMessageWrapper
        $promoMessageStyle={promoMessageStyle}
        data-autotag={dataAutotag}
      >
        {shouldShowOfferLabel && (
          <span dangerouslySetInnerHTML={{ __html: `${offerLabel}:` }} />
        )}
        {shouldShowPromoLabel && (
          <DynamicFormattedMessage
            id={defaultGetPromoCodeMessageId(promoCode)}
            defaultMessage={promoLabel}
          />
        )}
        {hasColon || !showPromoPrice ? null : ': '}
      </PromoMessageWrapper>
      {showPromoPrice && (
        <ProductPrice
          promoPriceStyle={promoPriceStyle}
          showPromoPrice
          showVipPrice={false}
          data-autotag="pdp-promo-price"
        />
      )}
    </Wrapper>
  );
}

PromoMessage.propTypes = {
  'data-autotag': PropTypes.string,
  getPromoCodeMessageId: PropTypes.func,
  product: PropTypes.object,
  promoMessageStyle: PropTypes.any,
  promoPriceStyle: PropTypes.any,
  showPromoPrice: PropTypes.bool,
};

export default PromoMessage;
